import React, { useEffect, useRef, useState } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import { toast } from 'react-toastify';
import Icon from '@mdi/react';
import { mdiDownload } from '@mdi/js';
import { useQuery } from '@tanstack/react-query';
import { printExpenseClaimAttachment } from '../../../store/apis/expenseService';
import { SpinnerLoading } from '../../Common';
import { useExpenseClaimUtilCtx } from '../../../pages/ExpenseClaims/context/ExpenseClaimUtilProvider';
import { FileUtil } from '../../../utils';
import { FILE_EXTENSIONS } from './constants';
import './PrintModal.scss';

export default function PrintModal({ isOpen, toggle, claimNo }) {
  const { printExpenseClaimAttachmentMutation } = useExpenseClaimUtilCtx();
  const iframeRef = useRef(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const { data: pdfBlob, isLoading } = useQuery({
    queryKey: ['printExpenseClaimAttachment', claimNo],
    queryFn: () => printExpenseClaimAttachment({ claimNo, type: 'PDF' }),
    enabled: isOpen && !!claimNo,
  });

  useEffect(() => {
    if (pdfBlob) {
      setPdfUrl(URL.createObjectURL(pdfBlob));
    }
  }, [pdfBlob]);

  useEffect(() => {
    if (!isOpen && iframeRef.current) {
      iframeRef.current.src = '';
      iframeRef.current = null;
    }
  }, [isOpen]);

  const handleFileDownload = async fileType => {
    const responseBlob = await printExpenseClaimAttachmentMutation.mutateAsync({
      claimNo,
      type: fileType,
    });
    if (responseBlob) {
      const fileName = `Expense_Claim_${claimNo}.${FILE_EXTENSIONS[fileType]}`;
      FileUtil.downloadBlobFile(responseBlob, fileName);
    } else {
      toast.error(`Failed to download ${fileType} file for claim ${claimNo}.`, {
        className: 'toast-error',
      });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      fullscreen
      className="full-screen-modal"
    >
      <ModalHeader toggle={toggle}>Print Preview</ModalHeader>
      <hr className="print-modal-hr" />
      <ModalBody className="p-0">
        {isLoading && (
          <SpinnerLoading
            isLoading={isLoading}
            height="50vh"
            spinnerSize="2rem"
            color="black"
          />
        )}
        {!isLoading && !pdfUrl && (
          <div className="text-center p-5">
            <p>No PDF available for this claim.</p>
          </div>
        )}
        <div className="d-flex justify-content-between align-items-center px-3 py-2">
          <span className="px-2 fw-semibold my-1 fs-5">
            Claim No: {claimNo}
          </span>
          <Dropdown
            isOpen={isDropdownOpen}
            toggle={() => setIsDropdownOpen(!isDropdownOpen)}
          >
            <DropdownToggle caret color="primary" className="py-1 px-1">
              <Icon path={mdiDownload} size={1} className="me-1" />
              Download
            </DropdownToggle>
            <DropdownMenu>
              <DropdownItem onClick={() => handleFileDownload('EXCEL')}>
                EXCEL
              </DropdownItem>
              <DropdownItem onClick={() => handleFileDownload('PDF')}>
                PDF
              </DropdownItem>
              <DropdownItem onClick={() => handleFileDownload('DOCX')}>
                DOCX
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </div>
        {pdfUrl && (
          <iframe
            ref={iframeRef}
            src={`${pdfUrl}#zoom=150&view=Fit`}
            title="PDF to print"
            className="print-modal-iframe"
          />
        )}
      </ModalBody>
    </Modal>
  );
}
